import { Fragment, useMemo, type ReactNode, type UIEvent } from "react";
import { Loader2 } from "lucide-react";
import type { LockupClientAssignmentRow, LockupValueBucket } from "@/analytics/lockup.api";
import { formatCurrency, formatText, getTableMinWidth } from "@/lib/drillThroughTableNormalizers";
import type { AgeBand } from "@/types/drillThroughTable";
import { getAriaSort, SortableColumnHeader } from "@/components/SortableColumnHeader";
import type { SortDir, SortState } from "@/lib/tableSort";

type LockupClientAssignmentTableProps = {
  title: string;
  rows: LockupClientAssignmentRow[];
  ageBands: AgeBand[];
  loading?: boolean;
  loadingMessage?: string;
  refetching?: boolean;
  error?: string | null;
  totalRows?: number;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  sort?: SortState;
  onSort?: (key: string, direction: SortDir | null) => void;
};

type ValueColumn = (AgeBand & { sortKey?: string }) | { key: "total"; label: string; sortKey?: string };

type ClientGroup = {
  key: string;
  clientName: string | null | undefined;
  assignments: LockupClientAssignmentRow[];
};

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(" ");

const AGE_BAND_CELL_COLORS: Record<string, string> = {
  age_0_30: "bg-[#2f8f9d]/75 text-slate-950",
  age_31_60: "bg-[#78b760]/75 text-slate-950",
  age_61_90: "bg-[#d7bd45]/80 text-slate-950",
  age_91_120: "bg-[#d99355]/75 text-slate-950",
  age_120_180: "bg-[#cf777f]/75 text-slate-950",
  age_180_plus: "bg-[#b84f55]/80 text-white",
};

const ROW_HEIGHT_PX = 40;
const PREFETCH_ROWS_FROM_BOTTOM = 10;

const BREAKDOWN_TYPES: Array<{ label: string; key: "wip" | "aged_debt" | "lockup" }> = [
  { label: "WIP", key: "wip" },
  { label: "Debt", key: "aged_debt" },
  { label: "Lockup", key: "lockup" },
];

function groupRowsByClient(rows: LockupClientAssignmentRow[]): ClientGroup[] {
  const groupsByName = new Map<string, ClientGroup>();
  const orderedGroups: ClientGroup[] = [];
  rows.forEach((row, index) => {
    const key = row.client_name ?? "";
    let group = groupsByName.get(key);
    if (!group) {
      group = { key: `${key}-${index}`, clientName: row.client_name, assignments: [] };
      groupsByName.set(key, group);
      orderedGroups.push(group);
    }
    group.assignments.push(row);
  });
  return orderedGroups;
}

function sumLockupColumns(assignments: LockupClientAssignmentRow[], valueColumns: ValueColumn[]): LockupValueBucket {
  const totals: Record<string, number> = {};
  for (const column of valueColumns) {
    totals[column.key] = assignments.reduce((sum, assignment) => sum + (Number(assignment.lockup?.[column.key]) || 0), 0);
  }
  return totals as LockupValueBucket;
}

export function LockupClientAssignmentTable({
  title,
  rows,
  ageBands,
  loading = false,
  loadingMessage,
  refetching = false,
  error = null,
  totalRows,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  sort,
  onSort,
}: LockupClientAssignmentTableProps) {
  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || loadingMore) return;
    const element = event.currentTarget;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (distanceFromBottom <= PREFETCH_ROWS_FROM_BOTTOM * ROW_HEIGHT_PX) onLoadMore();
  };

  const valueColumns: ValueColumn[] = [
    ...ageBands.map((band) => ({ ...band, sortKey: band.key })),
    { key: "total", label: "Total", sortKey: "total" },
  ];
  const clientGroups = useMemo(() => groupRowsByClient(rows), [rows]);
  const identityWidth = "17%";
  const typeWidth = "8%";
  const valueWidth = "8.28%";
  const columnCount = 2 + 1 + valueColumns.length;

  return (
    <section className="overflow-hidden rounded-lg border bg-card shadow-sm" aria-label={title}>
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2.5">
        <h2 className="text-sm font-semibold text-card-foreground">{title}</h2>
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          {refetching ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> : null}
          {loading ? "Loading" : totalRows !== undefined ? `${rows.length} / ${totalRows} assignments` : `${rows.length} assignments`}
        </span>
      </div>

      <div
        className={cx(
          "relative max-h-[calc(64vh-5rem)] min-h-[340px] overflow-auto",
          refetching && "opacity-60 transition-opacity",
        )}
        onScroll={handleScroll}
      >
        {loading && loadingMessage ? (
          <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-card/60">
            <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm text-muted-foreground shadow-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {loadingMessage}
            </div>
          </div>
        ) : null}

        <table
          className="w-full table-fixed border-separate border-spacing-0 text-xs"
          style={{ minWidth: getTableMinWidth(3, valueColumns.length) }}
        >
          <colgroup>
            <col style={{ width: identityWidth }} />
            <col style={{ width: identityWidth }} />
            <col style={{ width: typeWidth }} />
            {valueColumns.map((column) => <col key={column.key} style={{ width: valueWidth }} />)}
          </colgroup>
          <thead>
            <tr>
              <th
                className="sticky left-0 top-0 z-30 whitespace-nowrap border-r border-white/10 bg-accent px-3 py-2.5 text-left font-bold leading-tight text-accent-foreground"
                aria-sort={getAriaSort("name", sort)}
              >
                <SortableColumnHeader label="Client Name" sortKey="name" sortType="text" sort={sort} onSort={onSort} />
              </th>
              <th className="sticky top-0 z-20 whitespace-nowrap border-r border-white/10 bg-accent px-3 py-2.5 text-left font-bold leading-tight text-accent-foreground">
                Assignment
              </th>
              <th className="sticky top-0 z-20 whitespace-nowrap border-r border-white/10 bg-accent px-3 py-2.5 text-left font-bold leading-tight text-accent-foreground">
                Type
              </th>
              {valueColumns.map((column) => (
                <th
                  key={column.key}
                  className="sticky top-0 z-20 whitespace-nowrap border-r border-white/10 bg-accent px-3 py-2.5 text-center font-bold leading-tight text-accent-foreground"
                  aria-sort={getAriaSort(column.sortKey, sort)}
                >
                  <SortableColumnHeader label={column.label} sortKey={column.sortKey} sort={sort} onSort={onSort} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <LoadingRows columnCount={columnCount} />
            ) : clientGroups.length === 0 && error ? (
              <tr>
                <td className="h-44 bg-card text-center text-destructive" colSpan={columnCount}>
                  {error}
                </td>
              </tr>
            ) : clientGroups.length === 0 ? (
              <tr>
                <td className="h-44 bg-card text-center text-muted-foreground" colSpan={columnCount}>
                  No records found
                </td>
              </tr>
            ) : (
              clientGroups.map((group, groupIndex) => (
                <ClientBlock key={group.key} group={group} shaded={groupIndex % 2 === 1} valueColumns={valueColumns} />
              ))
            )}
            {!loading && loadingMore ? (
              <tr>
                <td className="border-b bg-card px-3 py-2.5 text-center text-muted-foreground" colSpan={columnCount}>
                  <span className="inline-flex items-center gap-2 text-xs">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading more...
                  </span>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ClientBlock({
  group,
  shaded,
  valueColumns,
}: {
  group: ClientGroup;
  shaded: boolean;
  valueColumns: ValueColumn[];
}) {
  const { assignments } = group;
  const clientRowSpan = assignments.length * BREAKDOWN_TYPES.length + 1;
  const totalLockup = sumLockupColumns(assignments, valueColumns);

  const clientNameCell = (
    <td
      rowSpan={clientRowSpan}
      className={cx(
        "sticky left-0 z-10 border-b border-r px-3 py-2.5 align-top font-semibold leading-snug text-foreground shadow-[1px_0_0_hsl(var(--border))]",
        shaded ? "bg-muted" : "bg-card",
      )}
    >
      {formatText(group.clientName)}
    </td>
  );

  return (
    <Fragment>
      {assignments.map((assignment, assignmentIndex) => (
        <AssignmentRows
          key={getAssignmentKey(assignment, assignmentIndex)}
          assignment={assignment}
          shaded={shaded}
          valueColumns={valueColumns}
          clientNameCell={assignmentIndex === 0 ? clientNameCell : null}
        />
      ))}
      <tr className={cx("bg-primary/10 hover:bg-secondary/50", shaded && "bg-muted/35")}>
        {assignments.length === 0 ? clientNameCell : null}
        <td className="border-b border-r px-3 py-2.5" />
        <td className="border-b border-r px-3 py-2.5 font-bold uppercase text-foreground">Total Lockup</td>
        <ValueCells values={totalLockup} valueColumns={valueColumns} bold />
      </tr>
    </Fragment>
  );
}

function AssignmentRows({
  assignment,
  shaded,
  valueColumns,
  clientNameCell,
}: {
  assignment: LockupClientAssignmentRow;
  shaded: boolean;
  valueColumns: ValueColumn[];
  clientNameCell: ReactNode;
}) {
  return (
    <>
      {BREAKDOWN_TYPES.map((breakdown, breakdownIndex) => {
        const isLockup = breakdown.key === "lockup";
        return (
          <tr key={breakdown.key} className={cx("hover:bg-secondary/50", shaded && "bg-muted/35", isLockup && "bg-primary/5")}>
            {breakdownIndex === 0 ? clientNameCell : null}
            {breakdownIndex === 0 ? (
              <td
                rowSpan={BREAKDOWN_TYPES.length}
                className="border-b border-r px-3 py-2.5 align-top font-semibold leading-snug text-foreground"
              >
                {formatText(assignment.assignment_name)}
              </td>
            ) : null}
            <td className={cx("border-b border-r px-3 py-2.5 font-medium text-foreground", isLockup && "font-bold")}>
              {breakdown.label}
            </td>
            <ValueCells values={assignment[breakdown.key]} valueColumns={valueColumns} />
          </tr>
        );
      })}
    </>
  );
}

function ValueCells({
  values,
  valueColumns,
  bold = false,
}: {
  values: LockupValueBucket | undefined;
  valueColumns: ValueColumn[];
  bold?: boolean;
}) {
  return (
    <>
      {valueColumns.map((column) => (
        <td
          key={column.key}
          className={cx(
            "border-b border-r px-3 py-2.5 text-center tabular-nums text-foreground",
            (bold || column.key === "total") && "font-bold",
            AGE_BAND_CELL_COLORS[column.key],
          )}
        >
          {formatCurrency(values?.[column.key], 0)}
        </td>
      ))}
    </>
  );
}

function LoadingRows({ columnCount }: { columnCount: number }) {
  return (
    <>
      {Array.from({ length: 6 }).map((_, rowIndex) => (
        <tr key={rowIndex} className="even:bg-muted/35">
          {Array.from({ length: columnCount }).map((__, columnIndex) => (
            <td
              key={columnIndex}
              className={cx(
                "border-b border-r px-3 py-2.5",
                columnIndex === 0 && "sticky left-0 z-10 bg-card shadow-[1px_0_0_hsl(var(--border))]",
                columnIndex === 0 && rowIndex % 2 === 1 && "bg-muted",
              )}
            >
              <div className={cx("h-4 animate-pulse rounded bg-muted", columnIndex < 2 ? "w-24" : "mx-auto w-20")} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function getAssignmentKey(assignment: LockupClientAssignmentRow, assignmentIndex: number): string {
  return [assignment.assignment_code, assignment.assignment_name, assignmentIndex].filter(Boolean).join("-");
}
